'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import cx from 'classnames';
import {
  CatQuantityPicker,
  BlocksQuantityPickerChangeEvent,
  CommonMessageTypes,
  CatNumericInput
} from 'components/blocks-components';
import { useQueryClient } from '@tanstack/react-query';
import { useProductDetailPageContext } from '../../context/product-detail';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import {
  PARTS_MAX_QUANTITY,
  PARTS_MIN_QUANTITY
} from 'constants/parts-diagram';
import { PAGE_BREAKPOINTS } from 'constants/common';
import styles from './quantity-picker.module.scss';

interface QuantityPickerProps {
  isUpdatePDPHeaderMobile: boolean;
}
const QuantityPicker: FC<QuantityPickerProps> = ({
  isUpdatePDPHeaderMobile = false
}) => {
  const { quantity: updatedQuantity, updateQuantity } =
    useProductDetailPageContext();
  const t = useTranslations();

  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const queryClient = useQueryClient();
  const [isDisabled, setIsDisabled] = useState(false);
  const quantityPickerStatus = updatedQuantity < PARTS_MIN_QUANTITY && 'error';
  const delayTimer = useRef(null);
  //clearing  the query cache when quantity changing,it will back to check availability button in check other locations drawer
  const clearAvailabilityCache = useCallback(() => {
    queryClient
      .getQueryCache()
      .getAll()
      .map((cache) => cache.queryKey)
      .filter((flt) => flt.includes('priceAndAvailability') && !!flt[2])
      .forEach((itm) => queryClient.removeQueries({ queryKey: itm }));
  }, [queryClient]);

  const onHandleQuantityPicker = useCallback(
    (e: BlocksQuantityPickerChangeEvent) => {
      const quantity = e.detail.value;
      const numericValue = quantity;

      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
      }
      delayTimer.current = setTimeout(() => {
        if (!isNaN(numericValue)) {
          if (numericValue > PARTS_MAX_QUANTITY) {
            updateQuantity(PARTS_MAX_QUANTITY);
            clearAvailabilityCache();
            setIsDisabled(true);
          } else {
            updateQuantity(numericValue);
          }
        }
      }, 1000);
    },
    [clearAvailabilityCache, updateQuantity]
  );

  useEffect(() => {
    if (isDisabled) {
      const timer = setTimeout(() => setIsDisabled(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isDisabled]);

  const showNewMobileQuantityInput = !isMD && isUpdatePDPHeaderMobile;
  return (
    <>
      {showNewMobileQuantityInput ? (
        <div className={styles['quantity-picker__mobile-component']}>
          <CatQuantityPicker
            data-testid="part-price-cat-quantity-picker"
            onBlQuantityPickerChange={onHandleQuantityPicker}
            label=""
            step={1}
            max={PARTS_MAX_QUANTITY}
            min={PARTS_MIN_QUANTITY}
            size="md"
            status={quantityPickerStatus as CommonMessageTypes}
            disabled={isDisabled}
            errorNote={
              updatedQuantity < PARTS_MIN_QUANTITY &&
              `${t('SC_PKG_QTY_PART_ERROR2')} (1)`
            }
            value={updatedQuantity}
          />
        </div>
      ) : (
        <div
          className={cx(
            'd-md-inline-block d-block',
            styles['add-cart-item-quantity__container']
          )}
        >
          <div className="d-none d-md-block">
            <CatNumericInput
              value={updatedQuantity}
              onBlNumericInputChange={onHandleQuantityPicker}
              hideLabel
              size="sm"
              min={PARTS_MIN_QUANTITY}
              max={PARTS_MAX_QUANTITY}
              data-testid="item-quantity-desktop"
              status={updatedQuantity < PARTS_MIN_QUANTITY ? 'error' : null}
              disabled={isDisabled}
            >
              {updatedQuantity < 1 && (
                <span slot="error">{`${t('SC_PKG_QTY_PART_ERROR2')} (1)`}</span>
              )}
            </CatNumericInput>
          </div>
          <div className="d-md-none">
            <CatNumericInput
              value={updatedQuantity}
              onBlNumericInputChange={onHandleQuantityPicker}
              hideLabel
              size="md"
              min={PARTS_MIN_QUANTITY}
              max={PARTS_MAX_QUANTITY}
              data-testid="item-quantity"
              status={updatedQuantity < 1 ? 'error' : null}
              disabled={isDisabled}
            >
              {updatedQuantity < PARTS_MIN_QUANTITY && (
                <span slot="error">{`${t('SC_PKG_QTY_PART_ERROR2')} (1)`}</span>
              )}
            </CatNumericInput>
          </div>
        </div>
      )}
    </>
  );
};

export default QuantityPicker;
